import { LoginDialog } from '@/components/web/auth/login-dialog';
import { clearPendingCartAction, executeCartAction, loadPendingCartAction } from '@/lib/pending-cart-action';
import { type SharedData } from '@/types';
import { usePage } from '@inertiajs/react';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

type GateAction = () => void | Promise<void>;

interface LoginGateContextType {
  /** Runs the action immediately when logged in, otherwise opens the login dialog and runs it after login. */
  requireLogin: (action: GateAction) => void;
}

const LoginGateContext = createContext<LoginGateContextType | undefined>(undefined);

export const useLoginGate = (): LoginGateContextType => {
  const context = useContext(LoginGateContext);
  if (context === undefined) {
    throw new Error('useLoginGate must be used within a LoginGateProvider');
  }
  return context;
};

interface LoginGateProviderProps {
  children: ReactNode;
}

export function LoginGateProvider({ children }: LoginGateProviderProps) {
  const { auth } = usePage<SharedData>().props;
  const user = auth?.user ?? null;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [redirectTo, setRedirectTo] = useState<string | undefined>(undefined);
  const pendingActionRef = useRef<GateAction | null>(null);
  const hasResumedRef = useRef(false);

  const requireLogin = useCallback(
    (action: GateAction) => {
      if (user) {
        void action();
        return;
      }

      pendingActionRef.current = action;
      // Social login leaves the page; send the user back to where they are now.
      setRedirectTo(window.location.pathname + window.location.search);
      setDialogOpen(true);
    },
    [user],
  );

  // Password-login path: shared props are already reloaded when this fires.
  const handleLoginSuccess = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action) {
      void action();
    }
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      // Dialog dismissed: drop the queued action and any saved cart action.
      pendingActionRef.current = null;
      clearPendingCartAction();
    }
  }, []);

  // Social-login path: the OAuth flow reloads the page, so resume the cart
  // action persisted in sessionStorage once the user is authenticated.
  useEffect(() => {
    if (!user || hasResumedRef.current) return;
    // The in-memory dialog flow is handling this action (password login).
    if (pendingActionRef.current) return;

    const pendingAction = loadPendingCartAction();
    if (!pendingAction) return;

    hasResumedRef.current = true;
    clearPendingCartAction();
    void executeCartAction(pendingAction);
  }, [user]);

  return (
    <LoginGateContext.Provider value={{ requireLogin }}>
      {children}
      <LoginDialog open={dialogOpen} onOpenChange={handleOpenChange} onSuccess={handleLoginSuccess} redirectTo={redirectTo} />
    </LoginGateContext.Provider>
  );
}
